import type { ButtonProps } from '@components/molecules';
import { Button, ButtonSize, ButtonVariant } from '@components/molecules';
import { cn, type CompositionProp } from '@utils';
import {
	DialogContent,
	type DialogContentPrimitiveProps,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../dialog';

type DialogSimpleLayoutProps = {
	icon?: React.ReactNode;
	title: CompositionProp;
	description: CompositionProp;
	primaryButton: CompositionProp;
	secondaryButton?: CompositionProp;
	primaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
	secondaryButtonProps?: Omit<ButtonProps, 'type' | 'onClick' | 'variant'>;
	onPrimaryAction: () => void;
	onSecondaryAction?: () => void;
} & DialogContentPrimitiveProps;

export const DialogSimpleLayout: React.FC<DialogSimpleLayoutProps> = ({
	icon,
	className,
	title,
	description,
	primaryButton,
	secondaryButton,
	primaryButtonProps,
	secondaryButtonProps,
	onPrimaryAction,
	onSecondaryAction,
	children,
	...props
}) => {
	const titleComponent = typeof title === 'string' ? <DialogTitle>{title}</DialogTitle> : (title as React.ReactNode);

	const descriptionComponent =
		typeof description === 'string' ? (
			<DialogDescription>{description}</DialogDescription>
		) : (
			<DialogDescription asChild>
				<div>{description}</div>
			</DialogDescription>
		);

	const primaryButtonComponent =
		typeof primaryButton === 'string' ? (
			<Button {...primaryButtonProps} size={ButtonSize.SM} type="submit" onClick={onPrimaryAction}>
				{primaryButton}
			</Button>
		) : (
			(primaryButton as React.ReactNode)
		);

	const secondaryButtonComponent =
		typeof secondaryButton === 'string' ? (
			<Button
				{...secondaryButtonProps}
				variant={ButtonVariant.SECONDARY}
				size={ButtonSize.SM}
				onClick={onSecondaryAction}
			>
				{secondaryButton}
			</Button>
		) : (
			(secondaryButton as React.ReactNode)
		);

	return (
		<DialogContent className={cn('max-h-[90vh] w-[520px] sm:max-w-[95vw]', className)} {...props}>
			<div>
				<DialogHeader className="grow-0 justify-items-center pb-4">
					<div className="self-center">{icon && icon}</div>
					{titleComponent}
					{descriptionComponent}
				</DialogHeader>
				{children && <div className="shrink overflow-y-auto px-10 pb-4 pt-4">{children}</div>}
				<DialogFooter className="grow-0 justify-center">
					<div className="flex flex-row space-x-4">
						{onSecondaryAction && secondaryButtonComponent}
						{primaryButtonComponent}
					</div>
				</DialogFooter>
			</div>
		</DialogContent>
	);
};
